<script>
import dayjs from 'dayjs';
import { mapMutations, mapState, mapGetters } from 'vuex';

import { GATEWAYS, handleNumberInput, sleep } from '@/utils';

const YALDEI_ASERES_YEMEI_TESHUVA_CAMPAIGN_ID = '5f5095f0-a0a9-11f1-80e4-5d465831d495';
const YALDEI_ASERES_YEMEI_TESHUVA_START_DATE = '2026-09-12';
const YALDEI_ASERES_YEMEI_TESHUVA_END_DATE = '2026-09-21';

export default {
  name: 'FormEndDate',
  props: {
    staticForm: {
      type: Boolean,
      default: false
    },
    formCampaignSettings: {
      type: Object,
      default: () => null
    },
    localFrequency: {
      type: String,
      default: null
    },
    localNumCharges: {
      type: Number,
      default: null
    },
    localStartDate: {
      type: String,
      default: null
    }
  },

  data() {
    return {
      handleNumberInput,
      expanded: false,
      showDateInput: false
    };
  },
  computed: {
    ...mapState('donation', {
      donation: (state) => state
    }),
    ...mapState('publicOrgSettings', ['settings']),
    ...mapState('campaign', {
      campaignSettings: (campaign) => campaign.settings
    }),
    ...mapGetters('donation', ['isRecurring', 'isStartDateToday']),
    // Since this component is currently also used for the static form which relies on the default campaign settings
    settingsComputed() {
      if (this.formCampaignSettings) {
        return this.formCampaignSettings;
      }
      return this.staticForm ? this.settings : this.campaignSettings;
    },
    frequency() {
      return this.staticForm ? this.localFrequency : this.donation.frequency;
    },
    numChargesFieldDisabled() {
      return this.settingsComputed?.numChargesDisabled || this.isYaldeiAseresYemeiTeshuvaCampaignOpen;
    },
    isYaldeiAseresYemeiTeshuvaCampaignOpen() {
      if (this.staticForm || this.donation.campaignId !== YALDEI_ASERES_YEMEI_TESHUVA_CAMPAIGN_ID) {
        return false;
      }

      return !dayjs().isAfter(dayjs(YALDEI_ASERES_YEMEI_TESHUVA_END_DATE), 'day');
    },
    startDateFieldDisabled() {
      return this.isYaldeiAseresYemeiTeshuvaCampaignOpen;
    },
    numCharges: {
      get() {
        return this.staticForm ? this.localNumCharges : this.donation.numCharges;
      },
      set(value) {
        if (this.staticForm) {
          this.$emit('update:local-num-charges', value);
          return;
        }
        this.SET_NUM_CHARGES(value);
      }
    },
    startDateValue: {
      get() {
        return this.staticForm ? this.localStartDate : this.donation.startDate;
      },
      set(value) {
        if (this.staticForm) {
          this.$emit('update:local-start-date', value);
          return;
        }
        this.SET_START_DATE(value);
      }
    },
    safeStartDate() {
      return this.startDateValue || dayjs().toISOString();
    },
    isRecurringDonation() {
      if (this.staticForm) {
        return this.frequency && this.frequency !== 'single';
      }
      return this.isRecurring;
    },
    isStartDateTodayComputed() {
      if (!this.staticForm) {
        return this.isStartDateToday;
      }

      if (!this.startDateValue) {
        return true;
      }
      return dayjs(this.startDateValue).isSame(dayjs(), 'day');
    },
    showStartDateField() {
      // GROW doesn't support start date in the future
      if (this.donation.CCGateway?.provider === GATEWAYS.GROW) {
        return false;
      }
      return true;
    },
    // Triggers validation check
    recurringEnd() {
      const { frequency } = this;
      return { frequency };
    },
    summary() {
      const date = dayjs(this.safeStartDate);
      const startDate = date.isSame(dayjs(), 'day') ? this.$t('donationFrequency.immediately') : date.format('MMM D, YYYY');

      if (!this.numCharges) {
        return `${this.$t('donationFrequency.startsOn')} ${startDate}, ${this.$t('donationFrequency.cancelAnytime')}`;
      }

      return `${this.$t('donationFrequency.startsOn')} ${startDate}, ${this.$t('donationFrequency.endsAfter')} ${this.numCharges} ${this.$t(
        'donationFrequency.charges'
      )}`;
    },
    today() {
      return dayjs().format('YYYY-MM-DD');
    },
    _30DaysAhead() {
      return dayjs().add(30, 'days').format('YYYY-MM-DD');
    },
    startDateComputed() {
      return dayjs(this.safeStartDate).format('YYYY-MM-DD');
    }
  },
  watch: {
    async expanded(value, newValue) {
      // focus field
      if (value && !newValue) {
        await sleep(100);
        this.$refs.numChargesInput.$el.querySelector('input').focus();
      }
    },
    'settingsComputed.numChargesPrefilled': {
      immediate: true,
      handler(value) {
        if (value) {
          this.numCharges = value;
        }
        this.applyYaldeiAseresYemeiTeshuvaSchedule();
      }
    },

    'donation.campaignId': {
      immediate: true,
      handler() {
        this.applyYaldeiAseresYemeiTeshuvaSchedule();
      }
    },

    // Reprefill after frequency change
    frequency: {
      handler(newValue) {
        if (newValue !== 'single' && this.settingsComputed?.numChargesPrefilled) {
          this.numCharges = this.settingsComputed.numChargesPrefilled;
        }
        this.applyYaldeiAseresYemeiTeshuvaSchedule();
      }
    }
  },

  methods: {
    applyYaldeiAseresYemeiTeshuvaSchedule() {
      if (this.staticForm || this.donation.campaignId !== YALDEI_ASERES_YEMEI_TESHUVA_CAMPAIGN_ID) {
        return;
      }

      const today = dayjs();
      const periodStart = dayjs(YALDEI_ASERES_YEMEI_TESHUVA_START_DATE);
      const periodEnd = dayjs(YALDEI_ASERES_YEMEI_TESHUVA_END_DATE);

      if (today.isAfter(periodEnd, 'day')) {
        return;
      }

      const firstChargeDate = today.isBefore(periodStart, 'day') ? periodStart : today;
      const firstChargeDateMidday = firstChargeDate.set('hour', 12).set('minute', 0).set('second', 0).set('millisecond', 0);

      this.numCharges = periodEnd.diff(firstChargeDate.startOf('day'), 'day') + 1;
      this.startDateValue = firstChargeDateMidday.toISOString();
      this.showDateInput = today.isBefore(periodStart, 'day');
    },
    handleStartDateChange(value) {
      const selectedDate = value?.target?.value || value;
      if (!selectedDate) {
        return;
      }
      const midday = dayjs(selectedDate).set('hour', 12).set('minute', 0).set('second', 0).set('millisecond', 0);
      this.showDateInput = true;
      this.startDateValue = midday.toISOString();
    },
    ...mapMutations('donation', ['SET_NUM_CHARGES', 'SET_START_DATE']),
    ...mapMutations('validation', ['SET_IS_RECURRING_END_VALID']),
    handleToggle() {
      this.expanded = !this.expanded;
    },
    openDatePicker(event) {
      const dateInput = event.target.closest('.formulate-input')?.querySelector('input[type="date"]');

      if (dateInput) {
        if (dateInput.showPicker) {
          dateInput.showPicker();
          return;
        }

        if (event.target !== dateInput) {
          dateInput.click();
        }
      }
    }
  }
};
</script>

<template>
  <section @click="handleToggle" v-if="isRecurringDonation" ref="recurringEndForm" class="form-end-date p-3 my-8 bg-off-white rounded-lg cursor-pointer">
    <div class="flex justify-between">
      <h4 class="body-text-extra-bold leading-normal">{{ $t('donationFrequency.duration') }}</h4>
      <app-icon height="8" name="arrow-up" type="svg" class="transition-transform duration-300 ease-in-out" :class="{ 'rotate-180': !expanded }" />
    </div>
    <div :class="{ 'mt-6': expanded }" class="duration-container">
      <transition name="expandx4">
        <div v-if="expanded" key="expanded">
          <div class="duration-fields">
            <div v-if="showStartDateField" class="duration-field">
              <label class="form-label">{{ $t('donationFrequency.firstCharge') }}</label>
              <!-- Show "Today" field until user clicks -->
              <div
                v-if="!showDateInput && isStartDateTodayComputed"
                class="start-date-today-control"
                :class="{ 'cursor-pointer': !startDateFieldDisabled, 'cursor-not-allowed opacity-50': startDateFieldDisabled }"
                @click.stop
              >
                <div class="today-field" aria-hidden="true">
                  <svg width="16" height="18" viewBox="0 0 16 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="0.5" y="2.5" width="15" height="15" rx="2.5" stroke="#B8B8D4" />
                    <path
                      d="M11.2002 2C12.8802 2 13.7206 2.00018 14.3623 2.32715C14.9265 2.61472 15.3853 3.07347 15.6729 3.6377C15.9998 4.27941 16 5.11978 16 6.7998V9H14.9795C14.9555 8.60783 14.9032 8.32929 14.7822 8.0918C14.5905 7.71554 14.2845 7.40951 13.9082 7.21777C13.4804 6.99979 12.9199 7 11.7998 7H4.2002C3.08009 7 2.51962 6.99979 2.0918 7.21777C1.71554 7.40951 1.40951 7.71554 1.21777 8.0918C1.09677 8.32929 1.04446 8.60783 1.02051 9H0V6.7998C4.91612e-10 5.11978 0.000180663 4.27941 0.327148 3.6377C0.614723 3.07347 1.07347 2.61472 1.6377 2.32715C2.27941 2.00018 3.11978 2 4.7998 2H11.2002Z"
                      fill="#B8B8D4"
                    />
                    <rect x="3" width="2" height="4" rx="1" fill="#B8B8D4" />
                    <rect x="11" width="2" height="4" rx="1" fill="#B8B8D4" />
                    <rect x="10" y="12" width="4" height="4" rx="1" fill="#B8B8D4" />
                  </svg>
                  <span class="today-text">{{ $t('donationFrequency.today') }}</span>
                </div>
                <formulate-input
                  :disabled="startDateFieldDisabled"
                  class="native-start-date-input"
                  :value="startDateComputed"
                  @change="handleStartDateChange"
                  id="start-date"
                  input-class="bg-white cursor-pointer"
                  type="date"
                  :min="today"
                  :max="_30DaysAhead"
                  @click="startDateFieldDisabled ? null : openDatePicker($event)"
                />
              </div>

              <!-- Show actual date input after user clicks -->
              <formulate-input
                @click.stop
                v-else
                :disabled="startDateFieldDisabled"
                :class="{ 'cursor-not-allowed user-select-none': startDateFieldDisabled }"
                :value="startDateComputed"
                @change="handleStartDateChange"
                id="start-date"
                input-class="bg-white cursor-pointer"
                type="date"
                :min="today"
                :max="_30DaysAhead"
                @click="startDateFieldDisabled ? null : openDatePicker($event)"
              />
            </div>

            <!-- Number of charges field (right side) -->
            <div class="duration-field">
              <formulate-input
                :disabled="numChargesFieldDisabled"
                :class="{ 'cursor-not-allowed user-select-none': numChargesFieldDisabled }"
                @click.stop
                @keyup.enter="expanded = false"
                @keydown="handleNumberInput($event, 3)"
                :label="$t('donationFrequency.numberOfCharges')"
                v-model.number="numCharges"
                validation="bail|optional|min:2|max:120"
                type="number"
                :validation-name="$t('donationFrequency.numberOfCharges')"
                max="120"
                min="2"
                step="1"
                id="number-of-charges"
                name="numberOfCharges"
                ref="numChargesInput"
              />
            </div>
          </div>
        </div>
      </transition>
      <div v-if="!expanded" class="text-black-01 text-sm">{{ summary }}</div>
    </div>
  </section>
</template>
